import React from "react";
import BrandMark from "./BrandMark";
import PaymentQr from "./PaymentQr";
import PreviewValue from "./PreviewValue";
import { invoiceSample, previewSample } from "../data/brandMock";
import { BrandDetails } from "../types/brand.types";
import {
  brandHandle,
  hasPaymentDetails,
  senderAddressLine,
  senderName,
} from "../previewFields";

/** The ink the design gives the account rows, as against the UPI pair. */
const ACCOUNT_INK = "text-[#00424B]";

/** One `Label: value` row in the bank details block. */
const BankRow: React.FC<{
  label: string;
  value: string;
  sample: string;
  /** Ink for the value; the account rows are drawn in the design's teal. */
  tone?: string;
}> = ({ label, value, sample, tone }) => (
  <div className="flex gap-1.5">
    <span className="text-[#80828D]">{label}</span>
    <PreviewValue
      value={value}
      sample={sample}
      className={`font-medium ${tone ?? "text-[#000000]"}`}
    />
  </div>
);

interface InvoicePreviewProps {
  details: BrandDetails;
}

/**
 * Specimen payment invoice. Everything that belongs to the dietitian — the
 * logo, the "From" block, the payment details — is wired to the form; the
 * client, plan and amount stay fixed sample data.
 */
const InvoicePreview: React.FC<InvoicePreviewProps> = ({ details }) => {
  // The bank block keeps its stand-ins only while none of it has been filled
  // in. From the first real row on, the rest shows blank — see
  // `hasPaymentDetails` for why the block is treated as one.
  const bankSample = (specimen: string) =>
    hasPaymentDetails(details) ? "" : specimen;

  return (
    <div className="flex h-full flex-col bg-white">
      {/* Fixed spacing throughout: the page is drawn at one canonical size and
          scaled by the panel, so it must not reflow at breakpoints. */}
      <div className="flex flex-1 flex-col p-7">
        <div className="flex items-start justify-between gap-4">
          {/* The brand the invoice is issued under, set under the logo rather
              than beside it: below, a logo that carries its own wordmark reads
              as the artwork it is with the name captioning it — alongside, the
              two would sit as one line and print the name twice. */}
          <div className="min-w-0 mt-3">
            {/* Same 2.5:1 ratio as before, just smaller — 100 × 40 rather
                than 120 × 48, so a short/wide crop isn't stretched thinner
                relative to its own box than it would be at the old size. */}
            <BrandMark
              details={details}
              size={40}
              width={100}
              squareSize={{ width: 52, height: 52 }}
            />
            <div className="mt-1.5 text-[13px] font-semibold text-[#111C2D]">
              <PreviewValue
                value={details.brandName}
                sample={previewSample.brandName}
              />
            </div>
          </div>
          <div className="flex-shrink-0 text-right">
            <div className="text-[13px] font-semibold text-[#111C2D]">
              Payment Invoice
            </div>
            <div className="mt-0.5 text-[8px] text-ink-subtle">
              {invoiceSample.number}
            </div>
            <div className="mt-0.5 text-[8px] text-ink-subtle">
              Date issued:{" "}
              <span className="font-semibold text-[#111C2D]">
                {invoiceSample.dateIssued}
              </span>
            </div>
          </div>
        </div>

        <div className="mt-8 flex items-start justify-between gap-6">
          <div className="min-w-0">
            <div className="text-[9px] text-ink-muted">To,</div>
            <div className="mt-0.5 text-[15px] font-medium text-[#111C2D]">
              {invoiceSample.billedTo.name}
            </div>
            <div className="mt-1 text-[8.5px] leading-[1.6] text-ink-muted">
              {invoiceSample.billedTo.address}
              <br />
              {invoiceSample.billedTo.cityLine}
            </div>
          </div>

          <div className="min-w-0 text-right">
            <div className="text-[9px] text-ink-muted">From,</div>
            <div className="mt-0.5 text-[15px] font-medium">
              <PreviewValue
                value={senderName(details)}
                sample={previewSample.fullName}
                className="text-[#111C2D]"
              />
            </div>
            <div className="mt-1 text-[8.5px] leading-[1.6] text-ink-muted">
              <PreviewValue
                value={details.address}
                sample={previewSample.address}
              />
              <br />
              <PreviewValue
                value={senderAddressLine(details)}
                sample={previewSample.cityLine}
              />
            </div>
          </div>
        </div>

        {/* Fixed column widths rather than `auto`: the two right-hand columns
            have to hold their place when a longer plan name arrives, which is
            what keeps "Duration" and "Amount" under their own headings. */}
        <div className="mt-9">
          <div className="grid grid-cols-[1fr_92px_64px] border-b border-line pb-2 text-[8.5px] text-ink-subtle">
            <span>Plan name</span>
            <span>Duration</span>
            <span className="text-right">Amount</span>
          </div>
          <div className="grid grid-cols-[1fr_92px_64px] pt-3 text-[9.5px] text-[#1E1F24]">
            <span>{invoiceSample.lineItem.planName}</span>
            <span>{invoiceSample.lineItem.duration}</span>
            <span className="text-right">{invoiceSample.lineItem.amount}</span>
          </div>
        </div>

        {/* Pushes the payable box to the foot of the page block, the way the
            document reads once a real plan list fills the space above it. */}
        <div className="flex-1" />

        <div className="flex items-center justify-between rounded-[6px] border border-line px-4 py-2.5">
          <span className="text-[12px] text-[#111C2D]">Payable amount</span>
          <span className="text-[16px] font-bold text-[#00424B]">
            {invoiceSample.payableAmount}
          </span>
        </div>
      </div>

      <div className="border-t border-[#A6EDF5] bg-[#F8FEFF] px-7 pt-6 pb-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 text-[8.5px] leading-[1.75]">
            <div className="mb-1.5 text-[8px] tracking-[0.08em] text-[#80828D]">
              BANK DETAILS
            </div>
            {/* Above the number, because that is the order the details are
                read back in when a payment is being set up — the name the
                account is held in, then the account. */}
            <BankRow
              label="A/c holder:"
              value={details.accountHolder}
              sample={bankSample(previewSample.accountHolder)}
              tone={ACCOUNT_INK}
            />
            <BankRow
              label="A/c:"
              value={details.accountNumber}
              sample={bankSample(previewSample.accountNumber)}
              tone={ACCOUNT_INK}
            />
            <BankRow
              label="Bank name:"
              value={details.bankName}
              sample={bankSample(previewSample.bankName)}
              tone={ACCOUNT_INK}
            />
            <BankRow
              label="IFSC:"
              value={details.ifsc}
              sample={bankSample(previewSample.ifsc)}
              tone={ACCOUNT_INK}
            />
            {/* The UPI pair is a second way to be paid, not more of the account
                above it — the rule is what says so. It spans the text block
                rather than the column, which is why it stops short of the QR. */}
            <div className="mt-1 border-t-[0.54px] border-[#DEEAED] pt-1">
              <BankRow
                label="UPI number:"
                value={details.upiNumber}
                sample={bankSample(previewSample.upiNumber)}
              />
               <div className="mt-1 border-t-[0.54px] border-[#DEEAED] pt-1"></div>
              <BankRow
                label="UPI ID:"
                value={details.upiId}
                sample={bankSample(previewSample.upiId)}
              />
            </div>
          </div>

          <div className="flex-shrink-0 text-center">
            <PaymentQr
              src={details.paymentQr?.dataUrl}
              className="w-[74px] h-[74px] bg-white "
            />
            <div className="mt-1 text-[9px] text-[#80828D]">
              Scan QR to pay
            </div>
          </div>
        </div>

        {/* Ruled off from the bank block above it: the contact line belongs to
            the brand, not to the payment details. */}
        <div className="mt-3 flex items-center justify-between gap-x-4 border-t border-[#DDEDF2] pt-3 text-[8.5px]">
          <PreviewValue
            value={details.phone}
            sample={previewSample.phone}
            className="text-ink-muted"
          />
          <PreviewValue
            value={details.email}
            sample={previewSample.email}
            className="text-ink-muted"
          />
          {/* <span className="text-ink-muted">
            naurist.com/
            <PreviewValue
              value={brandHandle(details)}
              sample={previewSample.handle}
              className="text-ink-muted"
            />
          </span> */}
        </div>
      </div>
    </div>
  );
};

export default InvoicePreview;
